import http from 'node:http';
import https from 'node:https';
import {SecureContextOptions} from 'node:tls';
import type {AddressInfo, Socket} from 'node:net';
import {readFileSync} from 'node:fs';
import {AsyncLocalStorage} from 'node:async_hooks';
import {IOGateway} from '@interopio/gateway';
import wsGateway from './gateway/ws/core.js';
import {compose} from './common/compose.js';
import {
    ExtendedHttpIncomingMessage,
    ExtendedHttpServerResponse,
    HttpServerRequest,
    HttpServerResponse
} from './server/exchange.js';
import getLogger from './logger.js';
import {localIp, portRange} from './server/address.js';
import * as monitoring from './server/monitoring.js';
import {GatewayServer} from '../gateway-server';
import serverHeader from './server/server-header.js';
import {configure, type RouteConfig} from './app/route.js';
import {httpSecurity} from './app/auth.js';
import {ServerConfigurer, ServerHttpResponse, ServerWebExchange} from '../types/web/server';
import {HttpHeadResponseDecorator, WebHttpHandlerBuilder} from "./server/handler.ts";
import {initRoute} from './server/ws.ts';
import {SocketRoute, webSockets} from "./server/socket.ts";
import {HttpStatus} from "./http/status.ts";

const logger = getLogger('app');

function secureContextOptions(ssl: GatewayServer.SslConfig): SecureContextOptions {
    const options: SecureContextOptions = {};
    if (ssl.key) options.key = readFileSync(ssl.key);
    if (ssl.cert) options.cert = readFileSync(ssl.cert);
    if (ssl.ca) options.ca = readFileSync(ssl.ca);
    return options;
}

type RequestListener<
    Request extends typeof ExtendedHttpIncomingMessage = typeof ExtendedHttpIncomingMessage,
    Response extends typeof ExtendedHttpServerResponse<InstanceType<Request>> = typeof ExtendedHttpServerResponse,
> = (req: InstanceType<Request>, resOrUpgradeHead: (InstanceType<Response> & { req: InstanceType<Request> }) | Buffer<ArrayBufferLike>) => void;

type ServerOptions = http.ServerOptions<typeof ExtendedHttpIncomingMessage, typeof ExtendedHttpServerResponse>;

async function createListener(builder: WebHttpHandlerBuilder,
                              onSocketError: (err: Error) => void): Promise<RequestListener> {

    const httpHandler = builder.build();
    return async (req: ExtendedHttpIncomingMessage, resOrUpgradeHead: ExtendedHttpServerResponse | Buffer<ArrayBufferLike>) => {
        req.socket.addListener('error', onSocketError);
        let res: ExtendedHttpServerResponse;
        if (resOrUpgradeHead instanceof ExtendedHttpServerResponse) {
            res = resOrUpgradeHead;
        }
        else {
            req.upgradeHead = resOrUpgradeHead;
            res = new ExtendedHttpServerResponse(req);
            res.assignSocket(req.socket);
        }
        const request = new HttpServerRequest(req);
        const response = new HttpServerResponse(res);
        const decoratedResponse: ServerHttpResponse = request.method === 'HEAD' ? new HttpHeadResponseDecorator(response) : response;

        await httpHandler(request, decoratedResponse)

    };
}

function promisify<T>(fn: (callback?: (err?: Error) => void) => T): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const r = fn((err?: Error) => {
            if (err) {
                reject(err);
            } else {
                resolve(r);
            }
        });
    });
}

function memoryMonitor(config?: GatewayServer.ServerConfig['memory']) {
    if (config) {
        return monitoring.start({
            memoryLimit: config.memory_limit,
            dumpLocation: config.dump_location,
            dumpPrefix: config.dump_prefix,
            reportInterval: config.report_interval,
            maxBackups: config.max_backups
        });
    }
}

async function initBuilder(context: RouteConfig) {
    const storage = context.storage;
    const security = await httpSecurity(context);
    // websocket upgrade handler
    const sockets = webSockets(context);
    const handler = compose<ServerWebExchange>(
        serverHeader(context.serverHeader),
        ...security,
        ...sockets,
        ...context.middleware,
        // health check
        async ({request, response}, next) => {
            if (request.method === 'GET' && request.path === '/health') {
                response.setStatusCode(HttpStatus.OK);
                const buffer = Buffer.from('UP', 'utf-8');
                response.headers.set('Content-Type', 'text/plain; charset=utf-8');
                await response.body(buffer);
            } else {
                await next();
            }
        },
        // home page
        async ({request, response}, next) => {
            if (request.method === 'GET' && request.path === '/') {
                response.setStatusCode(HttpStatus.OK);
                const buffer = Buffer.from('io.Gateway Server', 'utf-8');
                response.headers.set('Content-Type', 'text/plain; charset=utf-8');
                await response.body(buffer);
            } else {
                await next();
            }
        },
        // not found
        async ({response}, _next) => {
            response.setStatusCode(HttpStatus.NOT_FOUND);
            await response.end();
        }
    );

    return new WebHttpHandlerBuilder(handler).storage(storage);
}

export const Factory = async (options: GatewayServer.ServerConfig): Promise<GatewayServer.Server> => {
    const ssl = options.ssl;
    const createServer = ssl
        ? (options: ServerOptions, handler: RequestListener) => https.createServer({...options, ...secureContextOptions(ssl)}, handler)
        : (options: ServerOptions, handler: RequestListener) => http.createServer(options, handler);
    const monitor = memoryMonitor(options.memory);
    const context: RouteConfig = {
        middleware: [],
        corsConfig: options.cors,
        cors: [],
        authConfig: options.auth,
        authorize: [],
        storage: new AsyncLocalStorage<{ exchange: ServerWebExchange }>(),
        sockets: new Map<string, SocketRoute>()
    };
    const gw = IOGateway.Factory({...options.gateway});
    if (options.gateway) {
        const config = options.gateway;

        await configure(async (configurer: ServerConfigurer) => {
            configurer.socket({path: config.route, factory: wsGateway.bind(gw), options: config})
        }, options, context);

    }
    if (options.app) {
        await configure(options.app, options, context)
    }

    const ports = portRange(options.port ?? 0);
    const host = options.host;
    const onSocketError = (err: Error) => logger.error(`socket error: ${err}`, err);
    const builder = await initBuilder(context);

    const listener = await createListener(builder, onSocketError);

    const serverP = new Promise<http.Server<typeof ExtendedHttpIncomingMessage, typeof ExtendedHttpServerResponse>>((resolve, reject) => {

        const server = createServer({
            IncomingMessage: ExtendedHttpIncomingMessage,
            ServerResponse: ExtendedHttpServerResponse,
        }, listener);

        server.on('error', (e: Error) => {
            if (e['code'] === 'EADDRINUSE') {
                logger.debug(`port ${e['port']} already in use on address ${e['address']}`);
                const {value: port} = ports.next();
                if (port) {
                    logger.info(`retry starting server on port ${port} and host ${host ?? '<unspecified>'}`);
                    server.close();
                    server.listen(port, host);
                } else {
                    logger.warn(`all configured port(s) ${options.port} are in use. closing...`);
                    server.close();
                    reject(e);
                }
            } else {
                logger.error(`server error: ${e.message}`, e);
                reject(e);
            }
        });
        server
            .on('listening', async () => {
                const info = server.address() as AddressInfo;

                for (const [path, route] of context.sockets) {
                    const endpoint = `${ssl ? 'wss' : 'ws'}://${localIp}:${info.port}${path}`;
                    await initRoute(path, route, endpoint, context.storage, onSocketError);
                }
                logger.info(`http server listening on ${info.address}:${info.port}`);
                resolve(server);
            });
        server
            .on('upgrade', (req: ExtendedHttpIncomingMessage, _socket: Socket, head: Buffer<ArrayBufferLike>) => {
                try {
                    listener(req, head);
                } catch (err) {
                    logger.error(`upgrade error: ${err}`, err);
                }
            })
            .on('close', async () => {
                logger.info(`http server closed.`);
            });
        try {
            const {value: port} = ports.next();
            server.listen(port, host);
        } catch (e) {
            logger.error(`error starting web socket server`, e);
            reject(e instanceof Error ? e : new Error(`listen failed: ${e}`));
        }
    });
    const server = await serverP;
    return new class implements GatewayServer.Server {
        readonly gateway = gw;

        async close(): Promise<void> {
            for (const [path, route] of context.sockets) {
                try {
                    if (route.close !== undefined) {
                        await route.close();
                    }
                } catch (e) {
                    logger.warn(`error closing route ${path}`, e);
                }
            }
            await promisify(cb => {
                server.closeAllConnections();
                server.close(cb);
            });
            if (monitor) {
                await monitoring.stop(monitor);
            }
            if (gw) {
                await gw.stop();
            }
        }
    }
}
